import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	json,
	type ActionFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import { Field } from '#app/components/forms.js'
import { sessionKey, signup } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.js'
import { checkHoneypot } from '#app/utils/honeypot.server.js'
import { authSessionStorage } from '#app/utils/session.server.js'
import { redirectWithToast } from '#app/utils/toast.server.js'
import {
	EmailSchema,
	NameSchema,
	PasswordSchema,
	UsernameSchema,
} from '#app/utils/user-validation.js'
import { verifySessionStorage } from '#app/utils/verification.server.js'

export const meta: MetaFunction = () => {
	return [{ title: 'Join | F.E.R.' }]
}

export const action = async ({ request }: ActionFunctionArgs) => {
	const formData = await request.formData()

	checkHoneypot(formData)

	const email = formData.get('email')
	const password = formData.get('password')
	const username = formData.get('username')
	const name = formData.get('name')
	const redirectTo = safeRedirect(
		formData.get('redirectTo'),
		`${username}/constancia/nueva`,
	)

	const submission = await parseWithZod(formData, {
		schema: intent =>
			SignupSchema.superRefine(async (data, ctx) => {
				const existingUser = await prisma.user.findUnique({
					where: { email: data.email },
					select: { id: true },
				})
				if (existingUser) {
					ctx.addIssue({
						path: ['email'],
						code: z.ZodIssueCode.custom,
						message: 'A user already exists with this email',
					})
					return
				}
			}).transform(async data => {
				if (intent !== null) return { ...data, session: null }
				const session = await signup({
					...data,
					// @ts-ignore
					email,
					// @ts-ignore
					username,
					// @ts-ignore
					password,
					// @ts-ignore
					name,
				})
				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session } = submission.value

	const authSession = await authSessionStorage.getSession(
		request.headers.get('cookie'),
	)
	authSession.set(sessionKey, session.id)
	const verifySession = await verifySessionStorage.getSession()
	const headers = new Headers()
	headers.append(
		'set-cookie',
		await authSessionStorage.commitSession(authSession, {
			expires: session.expirationDate,
		}),
	)
	headers.append(
		'set-cookie',
		await verifySessionStorage.destroySession(verifySession),
	)

	return redirectWithToast(
		safeRedirect(redirectTo),
		{ title: 'Bienvenido a F.E.R.', description: '¡Gracias por registrarte!' },
		{ headers },
	)
}

const SignupSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
	username: UsernameSchema,
	name: NameSchema,
})

export default function Join() {
	const actionData = useActionData<typeof action>()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo') ?? undefined

	const [form, fields] = useForm({
		id: 'join-form',
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex min-h-full flex-col justify-center">
			<div className="mx-auto w-full max-w-md px-8">
				<Form method="POST" {...getFormProps(form)}>
					<div>
						<Field
							labelProps={{
								htmlFor: fields.email.id,
								children: 'Email',
							}}
							inputProps={{
								...getInputProps(fields.email, { type: 'email' }),
								autoFocus: true,
								autoComplete: 'email',
							}}
							errors={fields.email.errors}
						/>
						<Field
							labelProps={{ htmlFor: fields.password.id, children: 'Password' }}
							inputProps={{
								...getInputProps(fields.password, { type: 'password' }),
								autoComplete: 'new-password',
							}}
							errors={fields.password.errors}
						/>
						<Field
							labelProps={{ htmlFor: fields.username.id, children: 'Username' }}
							inputProps={{
								...getInputProps(fields.username, { type: 'text' }),
								autoComplete: 'username',
								className: 'lowercase',
							}}
							errors={fields.username.errors}
						/>
						<Field
							labelProps={{ htmlFor: fields.name.id, children: 'Name' }}
							inputProps={{
								...getInputProps(fields.name, { type: 'text' }),
								autoComplete: 'name',
							}}
							errors={fields.name.errors}
						/>
					</div>
					<input type="hidden" name="redirectTo" value={redirectTo} />
					<button
						type="submit"
						className="w-full rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 focus:bg-blue-400"
					>
						Create Account
					</button>
					<div className="flex items-center justify-center">
						<div className="text-center text-sm text-gray-500">
							Already have an account?{' '}
							<Link
								className="text-blue-500 underline"
								to={{
									pathname: '/login',
									search: searchParams.toString(),
								}}
							>
								Log in
							</Link>
						</div>
					</div>
				</Form>
			</div>
		</div>
	)
}
